<template>
  <!-- 客户数据分布：按客户类型看客户构成，含统计明细列表 -->
  <div class="customer-distribution">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="customerDistribution">
        <template #default>
          <!--
            创建日期用 yyyy-MM-dd 的日期区间：后端按 [起, 止+1天) 取，只传日期不带时分秒，
            否则区间末端的次日会被一起算进来。
          -->
          <yq-date-range v-model:start-time="searchInfo.startTime" v-model:end-time="searchInfo.endTime" class="double-width">
            <template #prefix>建档时间</template>
          </yq-date-range>
          <el-select v-model="searchInfo.customerType" placeholder="全部" clearable>
            <template #prefix>客户类型</template>
            <el-option v-for="d in customerTypeList" :key="d.userCode" :label="d.dictName" :value="d.userCode"/>
          </el-select>
          <!-- 归属区域按省级前两位匹配，与后端 region 参数口径一致 -->
          <el-select v-model="searchInfo.region" placeholder="全部" clearable filterable>
            <template #prefix>归属区域</template>
            <el-option v-for="p in provinceList" :key="p.id" :label="p.name" :value="p.id"/>
          </el-select>
          <el-input v-model="searchInfo.searchKey" placeholder="请输入客户名称" @keydown.enter="resetSearch" clearable>
            <template #prefix>客户名称</template>
          </el-input>
        </template>
        <template #option>
          <page-button @click="resetSearchAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <list-table background-color="#FFF">
        <div v-loading="loading" class="dist-body">
          <div class="chart-card">
            <div class="section-title">客户数据分布</div>
            <div class="donut-wrap">
              <div ref="typeChart" class="donut-div"></div>
              <div class="donut-legend">
                <div v-for="row in typeRows" :key="row.name" class="lg-row">
                  <span class="lg-dot" :style="{background: row.color}"></span>
                  <span class="lg-name" :title="row.name">{{ row.name }}</span>
                  <span class="lg-val">{{ row.value }}</span>
                  <span class="lg-ratio">{{ row.ratio }}%</span>
                </div>
                <div v-if="!typeRows.length" class="lg-empty">暂无数据</div>
              </div>
            </div>

            <div class="divider-t"><span>统计明细列表</span></div>
            <el-table :data="typeRows" border size="small" show-summary :summary-method="summaryMethod"
                      empty-text="暂无数据">
              <el-table-column label="序号" width="70" align="center">
                <template v-slot="{$index}">{{ $index + 1 }}</template>
              </el-table-column>
              <el-table-column label="客户类型" prop="name">
                <template v-slot="{row}">
                  <span class="lg-dot inline" :style="{background: row.color}"></span>{{ row.name }}
                </template>
              </el-table-column>
              <el-table-column label="客户数量" prop="value" align="right" width="140">
                <template v-slot="{row}"><span class="num bold">{{ row.value }}</span></template>
              </el-table-column>
              <el-table-column label="客户数量占比" prop="ratio" align="right" width="160">
                <template v-slot="{row}"><span class="num">{{ row.ratio }}%</span></template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import customerStatApi from "@/api/dz-crm/partner/customerStatApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_TYPE} from "@/views/h5/crm/crmDictionary";
import {PROVINCE_LIST, toChartRows} from "@/views/h5/crm/customerStat";
import {useRoute, useRouter} from "vue-router";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "CustomerDistribution",
  components: {ListPage, ListTable, SearchCondition, PageButton, YqDateRange},
  data() {
    return {
      loading: false,
      searchInfo: {customerType: "", region: "", searchKey: ""},
      distribution: {},
      typeChart: null,
      customerTypeList: [],
      provinceList: PROVINCE_LIST,
      dictReady: false,
      dataReady: false,
    };
  },
  computed: {
    typeRows() {
      return toChartRows(this.distribution.typeStats, this.customerTypeList);
    },
  },
  beforeRouteLeave(to, from, next) {
    // 只读的统计页，不缓存页面数据
    next();
  },
  mounted() {
    commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE).then(list => {
      this.customerTypeList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadDistribution();
  },
  unmounted() {
    if (this.typeChart) {
      this.typeChart.dispose();
    }
  },
  methods: {
    async loadDistribution() {
      this.loading = true;
      try {
        this.distribution = await customerStatApi.distribution(this.buildParams()) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    buildParams() {
      const params = {};
      Object.keys(this.searchInfo).forEach(key => {
        const value = this.searchInfo[key];
        if (value === "" || value === null || value === undefined) {
          return;
        }
        params[key] = value;
      });
      return params;
    },
    resetSearch() {
      this.loadDistribution();
    },
    /** 重置：日期区间与三个条件一起清掉 */
    resetSearchAll() {
      this.searchInfo.customerType = "";
      this.searchInfo.region = "";
      this.searchInfo.searchKey = "";
      this.loadDistribution();
    },
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderChart();
      }
    },
    renderChart() {
      const dom = this.$refs.typeChart;
      if (!dom) {
        return;
      }
      if (!this.typeChart) {
        this.typeChart = markRaw(echarts.init(dom));
      }
      this.typeChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} ({d}%)"},
        title: {
          text: String(this.distribution.customerCount || 0),
          subtext: "客户数量",
          left: "center",
          top: "center",
          textStyle: {fontSize: 22, fontWeight: 600, color: "#1f2329"},
          subtextStyle: {fontSize: 11, color: "#8d97a6"},
        },
        series: [{
          type: "pie",
          radius: ["58%", "80%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: {show: false},
          labelLine: {show: false},
          itemStyle: {borderColor: "#fff", borderWidth: 2},
          data: this.typeRows.map(row => ({name: row.name, value: row.value, itemStyle: {color: row.color}})),
        }],
      });
    },
    /** 合计行：数量合计用后端返回的客户总数，占比固定 100% */
    summaryMethod({columns}) {
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "value") {
          return String(this.distribution.customerCount || 0);
        }
        if (column.property === "ratio") {
          return this.typeRows.length ? "100.00%" : "0.00%";
        }
        return "";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-distribution {
  height: 100%;

  .dist-body {
    padding: 10px 0;
  }

  .chart-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;
    }
  }

  // 饼图居中展示，图例放到下方横向排列
  .donut-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .donut-div {
    flex: none;
    width: 260px;
    height: 260px;
  }

  .donut-legend {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0 24px;
    width: 100%;
    max-width: 960px;
    max-height: 150px;
    overflow-y: auto;

    .lg-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 5px 0;
      font-size: 12px;
      color: #606266;

      .lg-dot {
        flex: none;
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }

      .lg-dot.inline {
        display: inline-block;
        margin-right: 6px;
      }

      .lg-name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .lg-val {
        flex: none;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: #303133;
      }

      .lg-ratio {
        flex: none;
        width: 56px;
        text-align: right;
        color: #909399;
        font-variant-numeric: tabular-nums;
      }
    }

    .lg-empty {
      padding: 8px 0;
      font-size: 12px;
      color: #c0c4cc;
    }
  }

  // 「统计明细列表」的分隔标题
  .divider-t {
    display: flex;
    align-items: center;
    margin: 18px 0 10px;
    font-size: 13px;
    color: #909399;

    &::before,
    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--yq-border-color, #ebeef5);
    }

    span {
      padding: 0 10px;
    }
  }

  .num {
    font-variant-numeric: tabular-nums;
  }

  .bold {
    font-weight: 600;
    color: #303133;
  }
}
</style>
